import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { FaChevronLeft, FaChevronRight, FaLeaf, FaHandHoldingHeart, FaGem } from 'react-icons/fa';

const About = ({ onReserveClick }) => {
  const [hoveredImpact, setHoveredImpact] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const isWheeling = useRef(false);

  const imageGallery = [
    {
      src: './images/hotel.jpeg',
      alt: 'Vista del Casa Hotel Eco Lodge Sumaq Mayu',
      label: 'Nuestro Refugio',
    },
    {
      src: './images/cama.webp',
      alt: 'Habitación Confortable',
      label: 'Confort Natural',
    },
    {
      src: './images/comida.jpg',
      alt: 'Comedor',
      label: 'Gastronomía Local',
    },
    {
      src: './images/casa.webp',
      alt: 'Instalaciones',
      label: 'Armonía',
    }
  ];

  const impactWords = [
    {
      text: 'NATURALEZA',
      color: '#34D399',
      icon: <FaLeaf />,
      description: 'Una inmersión total en la selva amazónica, respetando su ritmo y su belleza.'
    },
    {
      text: 'AUTENTICIDAD',
      color: '#6EE7B7',
      icon: <FaHandHoldingHeart />,
      description: 'Conexiones reales con la cultura local y tradiciones ancestrales que enriquecen la estancia.'
    },
    {
      text: 'LUJO',
      color: '#A7F3D0',
      icon: <FaGem />,
      description: 'Comodidad y diseño sostenible que se integran en el paisaje para un confort sin igual.'
    }
  ];

  const handleWheel = (e) => {
    if (window.innerWidth < 1024) return;
    if (isWheeling.current) return;

    const scrollDown = e.deltaY > 0;

    e.preventDefault();
    isWheeling.current = true;

    setActiveIndex((prev) => {
      if (scrollDown) {
        return (prev + 1) % imageGallery.length;
      } else {
        return (prev - 1 + imageGallery.length) % imageGallery.length;
      }
    });

    setTimeout(() => {
      isWheeling.current = false;
    }, 800);
  };

  const handleDragEnd = (event, info) => {
    const swipeThreshold = 50;
    const swipePower = Math.abs(info.velocity.x) * 0.1;

    if (info.offset.x < -swipeThreshold || swipePower > 50) {
      setActiveIndex((prev) => (prev + 1) % imageGallery.length);
    } else if (info.offset.x > swipeThreshold || swipePower > 50) {
      setActiveIndex((prev) => (prev - 1 + imageGallery.length) % imageGallery.length);
    }
  };

  const paginate = (direction) => {
    setActiveIndex((prev) => {
      if (direction > 0) {
        return (prev + 1) % imageGallery.length;
      } else {
        return (prev - 1 + imageGallery.length) % imageGallery.length;
      }
    });
  };

  const getSliderProps = (index) => {
    const total = imageGallery.length;
    let diff = index - activeIndex;
    // Wrap around for circular navigation
    if (diff > total / 2) diff -= total;
    if (diff < -total / 2) diff += total;

    if (diff === 0) {
      return { x: '0%', scale: 1, opacity: 1, zIndex: 3, filter: 'brightness(1)' };
    } else if (diff === 1) {
      return { x: '78%', scale: 0.88, opacity: 0.5, zIndex: 2, filter: 'brightness(0.5)' };
    } else if (diff === -1) {
      return { x: '-78%', scale: 0.88, opacity: 0.5, zIndex: 2, filter: 'brightness(0.5)' };
    } else {
      const side = diff > 0 ? 1 : -1;
      return { x: `${side * 120}%`, scale: 0.8, opacity: 0, zIndex: 1, filter: 'brightness(0.3)' };
    }
  };

  return (
    <section
      id="about"
      onWheel={handleWheel}
      className="bg-kene-dark px-4 py-16 lg:py-24"
      style={{
        position: 'relative',
        minHeight: '100vh',
        zIndex: 10,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(10, 25, 15, 0.8)',
        zIndex: 1,
      }} />
      <div style={{
        position: 'relative',
        zIndex: 2,
        width: '100%',
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 1rem'
      }}>
        {/* Layout Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          {/* Left Side: Slider with Peek Gallery */}
          <div className="relative w-full">
            <motion.div
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              onDragEnd={handleDragEnd}
              className="relative w-full h-[260px] sm:h-[350px] lg:h-[540px] cursor-grab active:cursor-grabbing overflow-hidden"
              style={{ borderRadius: '24px' }}
            >
              {imageGallery.map((image, index) => {
                const sliderProps = getSliderProps(index);
                const isActive = index === activeIndex;
                return (
                  <motion.div
                    key={index}
                    animate={sliderProps}
                    transition={{
                      duration: 0.5,
                      ease: [0.4, 0, 0.2, 1],
                    }}
                    style={{
                      position: 'absolute',
                      top: '0',
                      left: '8%',
                      width: '84%',
                      height: '100%',
                      borderRadius: '20px',
                      overflow: 'hidden',
                      boxShadow: isActive
                        ? '0 20px 50px rgba(0, 0, 0, 0.5)'
                        : '0 10px 25px rgba(0, 0, 0, 0.3)',
                    }}
                  >
                    <img
                      src={image.src}
                      alt={image.alt}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      draggable="false"
                    />
                    <div style={{
                      position: 'absolute',
                      inset: 0,
                      background: isActive
                        ? 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.05) 50%, transparent 100%)'
                        : 'rgba(0,0,0,0.2)',
                    }} />

                    {/* Label + Counter */}
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: isActive ? 1 : 0, y: isActive ? 0 : 10 }}
                      transition={{ duration: 0.35, delay: 0.15 }}
                      style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        padding: '1.5rem 1.75rem',
                      }}
                    >
                      <p style={{
                        color: '#c4ad79',
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.2em',
                        marginBottom: '0.25rem',
                        fontWeight: '500',
                      }}>
                        {String(activeIndex + 1).padStart(2, '0')} / {String(imageGallery.length).padStart(2, '0')}
                      </p>
                      <h3 style={{
                        color: 'white',
                        fontSize: 'clamp(1.2rem, 2.5vw, 1.6rem)',
                        fontFamily: 'Playfair Display, serif',
                        fontWeight: 'bold',
                        margin: 0,
                        textShadow: '0 2px 12px rgba(0,0,0,0.6)',
                      }}>
                        {image.label}
                      </h3>
                    </motion.div>
                  </motion.div>
                );
              })}

              {/* Navigation Arrows */}
              <button
                onClick={() => paginate(-1)}
                className="absolute top-1/2 -translate-y-1/2 text-white p-2.5 rounded-full transition-opacity duration-300 group-hover:opacity-100"
                style={{
                  left: '10px',
                  zIndex: 10,
                  background: 'rgba(0, 0, 0, 0.45)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  cursor: 'pointer',
                }}
                aria-label="Imagen anterior"
              >
                <FaChevronLeft size={14} />
              </button>
              <button
                onClick={() => paginate(1)}
                className="absolute top-1/2 -translate-y-1/2 text-white p-2.5 rounded-full transition-opacity duration-300"
                style={{
                  right: '10px',
                  zIndex: 10,
                  background: 'rgba(0, 0, 0, 0.45)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  cursor: 'pointer',
                }}
                aria-label="Siguiente imagen"
              >
                <FaChevronRight size={14} />
              </button>
            </motion.div>

            {/* Dot Indicators */}
            <div className="flex justify-center gap-2.5 mt-3 lg:mt-5" style={{ position: 'relative', zIndex: 10 }}>
              {imageGallery.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveIndex(index)}
                  style={{
                    width: activeIndex === index ? '28px' : '8px',
                    height: '8px',
                    borderRadius: '100px',
                    background: activeIndex === index ? '#c4ad79' : 'rgba(255, 255, 255, 0.25)',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    padding: 0,
                    boxShadow: activeIndex === index ? '0 0 10px rgba(196, 173, 121, 0.4)' : 'none',
                  }}
                  aria-label={`Ir a imagen ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Right Side: Text */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="p-5 lg:p-10"
            style={{
              background: 'rgba(10, 25, 15, 0.5)',
              backdropFilter: 'blur(12px)',
              borderRadius: '24px',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}
          >
            <div style={{ marginBottom: '1.5rem' }}>
              {/* Tagline */}
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                style={{
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.3em',
                  color: '#c4ad79',
                  marginBottom: '0.75rem',
                  fontWeight: '500',
                }}
              >
                Sobre Nosotros
              </motion.p>

              <h1 style={{
                fontSize: 'clamp(2rem, 5vw, 3rem)',
                fontFamily: 'Playfair Display, serif',
                fontWeight: 'bold',
                color: '#F9FAFB',
                lineHeight: 1.1,
                margin: 0,
                letterSpacing: '-0.03em',
                textShadow: '0 4px 15px rgba(0,0,0,0.5)'
              }}>
                Sumaq Mayu
              </h1>

              {/* Decorative Gold Line */}
              <motion.div
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: 0.5, ease: 'easeOut' }}
                style={{
                  width: '60px',
                  height: '2px',
                  background: '#c4ad79',
                  transformOrigin: 'left',
                  borderRadius: '2px',
                  marginTop: '1rem',
                }}
              />
            </div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.8 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              style={{
                fontSize: 'clamp(1rem, 1.5vw, 1.15rem)',
                lineHeight: 1.7,
                marginBottom: '1.5rem',
                color: '#D1D5DB',
                maxWidth: '500px'
              }}
            >
              En el corazón de la Amazonía, Sumaq Mayu es más que un destino; es una invitación a reconectar. Ofrecemos un refugio donde el lujo sostenible se encuentra con la autenticidad de la selva.
            </motion.p>
            <div style={{ marginBottom: '1.5rem' }}>
              {impactWords.map((word, index) => (
                <motion.div
                  key={index}
                  onHoverStart={() => setHoveredImpact(index)}
                  onHoverEnd={() => setHoveredImpact(null)}
                  onClick={() => setHoveredImpact(hoveredImpact === index ? null : index)}
                  style={{ position: 'relative', padding: '1rem 0', cursor: 'pointer' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <motion.span
                      initial={{ opacity: 0, scale: 0 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: 0.6 + index * 0.15 }}
                      style={{
                        color: word.color,
                        fontSize: 'clamp(1.2rem, 3vw, 1.5rem)',
                        flexShrink: 0,
                      }}
                    >
                      {word.icon}
                    </motion.span>
                    <h2 className="text-2xl lg:text-5xl" style={{
                      fontWeight: '900',
                      lineHeight: 1,
                      color: word.color,
                      margin: 0,
                      fontFamily: 'system-ui, -apple-system, sans-serif',
                      letterSpacing: '-0.03em',
                      transition: 'color 0.3s'
                    }}>
                      {word.text}
                    </h2>
                  </div>
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: hoveredImpact === index ? 1 : 0, height: hoveredImpact === index ? 'auto' : 0 }}
                    transition={{ duration: 0.4, ease: 'easeInOut' }}
                    style={{ overflow: 'hidden' }}
                  >
                    <p style={{ color: '#9CA3AF', paddingTop: '0.5rem', paddingLeft: '2.25rem', lineHeight: 1.6 }}>
                      {word.description}
                    </p>
                  </motion.div>
                  <motion.div
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 0.8, delay: 0.7 + index * 0.2 }}
                    style={{
                      height: '2px',
                      background: 'rgba(255, 255, 255, 0.2)',
                      transformOrigin: 'left',
                      marginTop: '0.75rem',
                      borderRadius: '2px',
                    }}
                  />
                </motion.div>
              ))}
            </div>
            <motion.button
              onClick={onReserveClick}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 1.5 }}
              whileHover={{ scale: 1.05, boxShadow: '0 15px 35px rgba(45, 212, 191, 0.3)' }}
              whileTap={{ scale: 0.98 }}
              style={{
                alignSelf: 'flex-start',
                background: '#14B8A6',
                color: '#ffffff',
                padding: '1rem 2.5rem',
                fontSize: '0.875rem',
                fontWeight: '700',
                border: 'none',
                borderRadius: '100px',
                cursor: 'pointer',
              }}
            >
              Reservar Estancia
            </motion.button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
